import { useEffect, useState } from 'react';
import { firstMatch } from '../../themeHost/firstDrawing';

// When a menu folds behind its button. LightFunnels lets a merchant say which devices get the burger,
// per device; this is that choice against the two widths the whole style engine already breaks at,
// plus the two ends — always, and never. The 1024 and 640 are `BREAKPOINT_WIDTHS` in
// `style/styleCascade.ts`, spelled by hand because a media query cannot read a constant.

/** Where the row gives way to the button, exactly as `menuElement` offers it. */
export const FOLDS = ['tablet', 'mobile', 'always', 'never'] as const;
export type MenuFold = (typeof FOLDS)[number];

/** The width each fold starts at; the two ends have no width, they are answered outright. */
const QUERIES: Readonly<Record<MenuFold, string | null>> = {
  tablet: '(max-width: 1024px)',
  mobile: '(max-width: 640px)',
  always: null,
  never: null
};

// Reads the names a menu was stored with before folding had four answers, so an old page keeps its shape.
export function menuFoldOf(raw: unknown): MenuFold {
  if (raw === 'burger') return 'tablet';
  if (raw === 'wrap') return 'never';
  return (FOLDS as readonly string[]).includes(String(raw)) ? (raw as MenuFold) : 'tablet';
}

// Reports whether the menu is folded right now, and keeps reporting it as the window is resized.
export function useMenuFold(fold: MenuFold): boolean {
  const query = QUERIES[fold];
  // The first answer is the server drawing's own, so the page is taken over as drawn; the effect below reads the window.
  const [folded, setFolded] = useState(() => fold === 'always' || (query !== null && firstMatch(query)));

  useEffect(() => {
    if (query === null) { setFolded(fold === 'always'); return; }
    const media = window.matchMedia(query);
    // Reads whether the window is narrow enough to fold the menu now.
    const read = () => setFolded(media.matches);
    read();
    // Both, because a browser under viewport emulation — every builder preview — fires the resize and
    // not the media change, and a menu that stays folded after the window widens is a menu with no links.
    media.addEventListener('change', read);
    window.addEventListener('resize', read);
    return () => { media.removeEventListener('change', read); window.removeEventListener('resize', read); };
  }, [fold, query]);

  return folded;
}
